/** Feature-scoped adapters; imported only by the surfaces that use them. */
import * as React from "react";
import { useApiClient } from "@authe/ui-core/api";
import { useSession } from "@authe/ui-core/session";
import { createApiTeamsApi, withMemberNames, type TeamsApi } from "@authe/teams";
import { createApiOrgUsersSource } from "../orgUsersSource";

/**
 * The `teams-mgmt-api` port, with the shared display-name backfill.
 *
 * A team member row is a snapshot written at join time and almost nothing
 * writes a name into it: `build_seeded_team` is handed none, and an
 * invite-accept copies the EMAIL only. So a teammate appeared as
 * "admin@authe.io" in the roster directly under the organization list that
 * names them "Roel den Blanken".
 *
 * `withMemberNames` (in `@authe/teams`) does the filling, and the admin portal
 * uses the same one — it used to be a second copy over there that had drifted
 * into never firing. What is local is only WHERE the names come from: the org
 * users directory, plus the session, which is authoritative for the reader's
 * own row before any directory read.
 */
export function useTeamsApi(): TeamsApi {
  const client = useApiClient();
  const { session } = useSession();
  const selfId = session?.user.id ?? session?.claims.user_id ?? null;
  const selfName = session?.user.name ?? session?.names.userName ?? null;
  const selfEmail = session?.user.email ?? null;
  return React.useMemo<TeamsApi>(() => {
    const orgUsers = createApiOrgUsersSource(client);
    return withMemberNames(createApiTeamsApi(client), {
      listPeople: () => orgUsers.listMembers(),
      self: { id: selfId, name: selfName, email: selfEmail },
    });
  }, [client, selfId, selfName, selfEmail]);
}
